import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { IS_MOCK_MODE, DEMO_USER, STORAGE_KEYS } from '@/lib/mockMode'
import { trackEvent } from '@/utils/analytics'

const TOKEN_STORAGE_KEY = STORAGE_KEYS.auth

// "Remember me" chooses where the auth bundle lives: localStorage (survives
// tab close / browser restart) when remembered, else sessionStorage (cleared
// on tab close). Reads prefer localStorage so a remembered session wins.
function readStored() {
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(TOKEN_STORAGE_KEY)
      if (raw) return { value: JSON.parse(raw), remember: true }
    }
    if (typeof sessionStorage !== 'undefined') {
      const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY)
      if (raw) return { value: JSON.parse(raw), remember: false }
    }
  } catch { /* ignore */ }
  return { value: null, remember: false }
}

function persistStored(value, remember) {
  try {
    // Always clear both stores first so a token never lingers in the other.
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(TOKEN_STORAGE_KEY)
    if (typeof localStorage !== 'undefined') localStorage.removeItem(TOKEN_STORAGE_KEY)
    if (!value) return
    const store = remember && typeof localStorage !== 'undefined' ? localStorage : sessionStorage
    store?.setItem(TOKEN_STORAGE_KEY, JSON.stringify(value))
  } catch { /* ignore */ }
}

// Lazily import the real Cognito client so mock mode stays bundle-light
// and we never trigger the buffer/global polyfill issue when not needed.
async function loadCognito() {
  return await import('@/lib/cognito')
}

export const useAuthStore = defineStore('auth', () => {
  const stored = readStored()

  const user = ref(stored.value?.user || null)
  const idToken = ref(stored.value?.idToken || null)
  const accessToken = ref(stored.value?.accessToken || null)
  const refreshToken = ref(stored.value?.refreshToken || null)
  const rememberMe = ref(stored.remember)
  const isLoading = ref(false)
  const error = ref('')

  const isAuthenticated = computed(() => !!idToken.value && !!user.value)
  const isDemoUser = computed(() => user.value?.isDemo === true)

  function _persist() {
    if (!idToken.value) {
      persistStored(null, rememberMe.value)
      return
    }
    persistStored({
      user: user.value,
      idToken: idToken.value,
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
    }, rememberMe.value)
  }

  function _setSession(session) {
    user.value = { email: session.email, userId: session.userId, isDemo: !!session.isDemo }
    idToken.value = session.idToken
    accessToken.value = session.accessToken
    refreshToken.value = session.refreshToken
    _persist()
  }

  // ── Demo / mock-mode sign-in ────────────────────────────────
  async function signInDemo({ remember = false } = {}) {
    isLoading.value = true
    error.value = ''
    try {
      rememberMe.value = !!remember
      _setSession({
        email: DEMO_USER.email,
        userId: DEMO_USER.userId,
        isDemo: true,
        idToken: 'mock.demo.token',
        accessToken: 'mock.demo.access',
        refreshToken: 'mock.demo.refresh',
      })
      return user.value
    } finally {
      isLoading.value = false
    }
  }

  // ── Real Cognito flows (lazy import) ────────────────────────
  async function signIn(email, password, { remember = false } = {}) {
    if (IS_MOCK_MODE) {
      throw new Error('Real sign-in is not available in mock mode. Use the "Continue as demo user" button.')
    }
    isLoading.value = true
    error.value = ''
    try {
      rememberMe.value = !!remember
      const { cognitoSignIn } = await loadCognito()
      const session = await cognitoSignIn(email, password)
      _setSession(session)
      return user.value
    } catch (err) {
      error.value = err?.message || 'Sign in failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function signUp(email, password, clientMetadata = undefined) {
    if (IS_MOCK_MODE) {
      throw new Error('Real sign-up is not available in mock mode. Use the "Continue as demo user" button.')
    }
    isLoading.value = true
    error.value = ''
    try {
      const { cognitoSignUp } = await loadCognito()
      const result = await cognitoSignUp(email, password, clientMetadata)
      // Invite signups are auto-confirmed (no verify step) — count them here;
      // the normal path counts on confirmSignUp.
      if (result?.userConfirmed) trackEvent('signup')
      return result
    } catch (err) {
      error.value = err?.message || 'Sign up failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function confirmSignUp(email, code) {
    if (IS_MOCK_MODE) throw new Error('Real verification is not available in mock mode.')
    const { cognitoConfirmSignUp } = await loadCognito()
    const result = await cognitoConfirmSignUp(email, code)
    // Account is confirmed/active only after verification — count it here.
    trackEvent('signup')
    return result
  }

  async function refreshSession() {
    if (IS_MOCK_MODE || !refreshToken.value) return null
    try {
      const { cognitoRefreshSession } = await loadCognito()
      const session = await cognitoRefreshSession(refreshToken.value)
      _setSession(session)
      return session
    } catch (err) {
      await signOut()
      throw err
    }
  }

  async function restoreSession() {
    // In mock mode, sessionStorage already has whatever the demo flow set.
    if (IS_MOCK_MODE) return user.value
    if (!idToken.value) return null
    try {
      const { cognitoGetCurrentSession } = await loadCognito()
      const session = await cognitoGetCurrentSession()
      if (session) _setSession(session)
      return session
    } catch {
      await signOut()
      return null
    }
  }

  async function signOut() {
    if (!IS_MOCK_MODE) {
      try {
        const { cognitoSignOut } = await loadCognito()
        await cognitoSignOut()
      } catch { /* ignore */ }
    }
    user.value = null
    idToken.value = null
    accessToken.value = null
    refreshToken.value = null
    _persist()
  }

  return {
    user,
    idToken,
    accessToken,
    refreshToken,
    rememberMe,
    isLoading,
    error,
    isAuthenticated,
    isDemoUser,
    isMockMode: IS_MOCK_MODE,
    signIn,
    signInDemo,
    signUp,
    confirmSignUp,
    refreshSession,
    restoreSession,
    signOut,
  }
})
